import React from 'react';
import { Mail, Phone, MapPin, Instagram, Linkedin, Facebook } from 'lucide-react';
import { useLanguage } from '../i18n';

const Footer = () => {
  const { t } = useLanguage();
  const currentYear = new Date().getFullYear();

  const companyHrefs = ['#about', '#portfolio', '#testimonials', '#contact'];

  const socialLinks = [
    { icon: Linkedin, href: "https://www.linkedin.com/in/ndoole-bindu-1b03bb423?trk=contact-info", label: "LinkedIn" },
    { icon: Facebook, href: "https://www.facebook.com/profile.php?id=61592274500982", label: "Facebook" }
  ];

  const scrollToSection = (e, href) => {
    e.preventDefault();
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-gray-900 text-gray-300">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <div className="flex items-center space-x-2 mb-4">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#E9782A] to-[#d96820] flex items-center justify-center">
                <span className="text-white font-bold text-xl">F</span>
              </div>
              <span className="text-2xl font-bold text-white">Fegga</span>
            </div>
            <p className="text-gray-400 mb-6 leading-relaxed">
              {t.footer.description}
            </p>
            
            {/* Contact Info */}
            <div className="space-y-3">
              <div className="flex items-center space-x-3 text-sm">
                <Mail className="text-[#E9782A]" size={18} />
                <span data-testid="footer-email">feggaagency@gmail.com</span>
              </div>
              <div className="flex items-center space-x-3 text-sm">
                <Phone className="text-[#E9782A]" size={18} />
                <span data-testid="footer-phone">+243 895 534 241 (WhatsApp)</span>
              </div>
              <div className="flex items-center space-x-3 text-sm">
                <MapPin className="text-[#E9782A]" size={18} />
                <span data-testid="footer-address">{t.footer.address}</span>
              </div>
            </div>
          </div>

          {/* Services Column */}
          <div>
            <h3 className="text-white font-bold text-lg mb-4">{t.footer.servicesTitle}</h3>
            <ul className="space-y-3">
              {t.footer.serviceLinks.map((label) => (
                <li key={label}>
                  <a
                    href="#services"
                    onClick={(e) => scrollToSection(e, '#services')}
                    className="text-gray-400 hover:text-[#E9782A] transition-colors duration-200 text-sm"
                  >
                    {label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Column */}
          <div>
            <h3 className="text-white font-bold text-lg mb-4">{t.footer.companyTitle}</h3>
            <ul className="space-y-3">
              {t.footer.companyLinks.map((label, i) => (
                <li key={label}>
                  <a
                    href={companyHrefs[i]}
                    onClick={(e) => scrollToSection(e, companyHrefs[i])}
                    className="text-gray-400 hover:text-[#E9782A] transition-colors duration-200 text-sm"
                  >
                    {label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Countries Column */}
          <div>
            <h3 className="text-white font-bold text-lg mb-4">{t.footer.marketsTitle}</h3>
            <ul className="grid grid-cols-2 gap-x-4 gap-y-3" data-testid="footer-markets-list">
              {t.footer.markets.map((label) => (
                <li key={label}>
                  <a
                    href="#countries"
                    onClick={(e) => scrollToSection(e, '#countries')}
                    className="text-gray-400 hover:text-[#E9782A] transition-colors duration-200 text-sm"
                  >
                    {label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Social Links */}
        <div className="mt-12 pt-8 border-t border-gray-800">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="flex flex-wrap items-center gap-4 mb-4 md:mb-0">
              <a
                href="https://www.instagram.com/fegga.agency?igsh=MXcxc2I2MzZieHlqeA=="
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                data-testid="social-link-instagram"
                className="flex items-center space-x-2 px-4 py-2 rounded-full bg-gradient-to-r from-[#feda75] via-[#d62976] to-[#4f5bd5] text-white font-semibold text-sm hover:scale-105 transition-transform duration-200 shadow-lg"
              >
                <Instagram size={18} />
                <span>@fegga.agency</span>
              </a>
              {socialLinks.map((social) => {
                const Icon = social.icon;
                return (
                  <a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={social.label}
                    data-testid={`social-link-${social.label.toLowerCase()}`}
                    className="w-10 h-10 rounded-full bg-gray-800 hover:bg-[#E9782A] flex items-center justify-center transition-all duration-200 hover:scale-110"
                  >
                    <Icon size={18} className="text-gray-300 hover:text-white" />
                  </a>
                );
              })}
            </div>

            <div className="text-sm text-gray-400">
              © {currentYear} Fegga. {t.footer.rights}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800 bg-gray-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col md:flex-row items-center justify-between text-xs text-gray-500">
            <div className="mb-2 md:mb-0">
              {t.footer.madeWith}
            </div>
            <div className="flex space-x-6">
              <a href="#" className="hover:text-[#E9782A] transition-colors">
                {t.footer.privacy}
              </a>
              <a href="#" className="hover:text-[#E9782A] transition-colors">
                {t.footer.terms}
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
